import React, { useState, useCallback } from 'react';

const TransactionTable = ({
  transactions,
  onSelectedAllocationsChange,
  selectedAllocations,
  onTransactionClick,
}) => {
  const [selectAll, setSelectAll] = useState(false);

  // Filter out transactions where Current Delivery No and Suggested Delivery No are exactly the same
  const filteredTransactions = transactions.filter((transaction) => {
    // Keep transactions that don't have a suggestion (SuggestedTicketNo is null/empty)
    if (!transaction.SuggestedTicketNo) {
      return true;
    }

    // Keep transactions where Current Delivery No and Suggested Delivery No are different
    return transaction.CurrentDeliveryNo !== transaction.SuggestedTicketNo;
  });

  // Handle individual row selection
  const handleRowSelection = useCallback(
    (transaction, isSelected) => {
      let newSelected;
      if (isSelected) {
        newSelected = [...selectedAllocations, transaction];
      } else {
        newSelected = selectedAllocations.filter(
          (item) => item.ReceiptUID !== transaction.ReceiptUID
        );
      }
      onSelectedAllocationsChange(newSelected);
    },
    [selectedAllocations, onSelectedAllocationsChange]
  );

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      // Deselect all
      onSelectedAllocationsChange([]);
      setSelectAll(false);
    } else {
      // Select all filtered transactions that have suggestions
      const allWithSuggestions = filteredTransactions.filter((t) => t.SuggestedTicketNo);
      onSelectedAllocationsChange(allWithSuggestions);
      setSelectAll(true);
    }
  }, [selectAll, filteredTransactions, onSelectedAllocationsChange]);

  // Check if a transaction is selected
  const isSelected = useCallback(
    (transaction) => {
      return selectedAllocations.some((item) => item.ReceiptUID === transaction.ReceiptUID);
    },
    [selectedAllocations]
  );

  // Handle row click (excluding checkbox column)
  const handleRowClick = useCallback(
    (transaction, event) => {
      // Don't trigger row click if clicking on checkbox
      if (event.target.type === 'checkbox') {
        return;
      }

      if (onTransactionClick) {
        onTransactionClick(transaction);
      }
    },
    [onTransactionClick]
  );

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Get timing color based on hours difference
  const getTimingColor = (hoursDiff) => {
    if (hoursDiff === null || hoursDiff === undefined) return 'text-gray-500';
    if (hoursDiff <= 2) return 'text-green-600';
    if (hoursDiff <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Get confidence indicator
  const getConfidenceIndicator = (transaction) => {
    if (!transaction.SuggestedTicketNo) return 'No Match';

    const hoursDiff = transaction.HoursDifference;
    const weightVariance = transaction.WeightVariance || 0;

    if (hoursDiff <= 2 && weightVariance <= 1) return 'High';
    if (hoursDiff <= 6 && weightVariance <= 5) return 'Medium';
    return 'Low';
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'High':
        return 'text-green-600 bg-green-50';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'Low':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-surface p-8 rounded-lg shadow-md text-center">
        <p className="text-gray-500">No transactions to display</p>
      </div>
    );
  }

  if (filteredTransactions.length === 0) {
    return (
      <div className="bg-surface p-8 rounded-lg shadow-md text-center">
        <p className="text-gray-500">No transactions to display after filtering</p>
        <p className="text-sm text-gray-400 mt-2">
          All transactions have identical Current and Suggested Delivery Numbers
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Transaction Allocation Suggestions ({filteredTransactions.length} of{' '}
            {transactions.length} transactions)
          </h3>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2 text-sm text-gray-700">Select All</span>
            </label>
            <span className="text-sm text-gray-500">{selectedAllocations.length} selected</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Select
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reg No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Receipt Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Delivery No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Suggested Delivery No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dispatch Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours Diff
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Weight Variance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Confidence
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-gray-200">
            {filteredTransactions.map((transaction, index) => {
              const selected = isSelected(transaction);
              const confidence = getConfidenceIndicator(transaction);
              const hasSuggestion = !!transaction.SuggestedTicketNo;

              return (
                <tr
                  key={transaction.ReceiptUID || index}
                  className={`${selected ? 'bg-blue-50' : 'hover:bg-gray-50'} ${!hasSuggestion ? 'opacity-60' : ''} cursor-pointer`}
                  onClick={(e) => handleRowClick(transaction, e)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => handleRowSelection(transaction, e.target.checked)}
                      disabled={!hasSuggestion}
                      className="form-checkbox h-4 w-4 text-primary-600 disabled:opacity-50"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {transaction.reg_no || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(transaction.ReceiptTimestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.CurrentDeliveryNo || 'Empty'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {transaction.SuggestedTicketNo || 'No suggestion'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.DispatchTimestamp
                      ? formatTimestamp(transaction.DispatchTimestamp)
                      : 'N/A'}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${getTimingColor(transaction.HoursDifference)}`}
                  >
                    {transaction.HoursDifference !== null &&
                    transaction.HoursDifference !== undefined
                      ? `${transaction.HoursDifference.toFixed(1)}h`
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.WeightVariance !== null && transaction.WeightVariance !== undefined
                      ? `${transaction.WeightVariance.toFixed(2)}t`
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceColor(confidence)}`}
                    >
                      {confidence}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <div>
            <span className="font-medium">
              {filteredTransactions.filter((t) => t.SuggestedTicketNo).length}
            </span>{' '}
            transactions with suggestions,
            <span className="font-medium ml-1">
              {filteredTransactions.filter((t) => !t.SuggestedTicketNo).length}
            </span>{' '}
            without suggestions
            {transactions.length !== filteredTransactions.length && (
              <span className="text-xs text-gray-500 ml-2">
                ({transactions.length - filteredTransactions.length} filtered out - identical
                delivery numbers)
              </span>
            )}
          </div>
          <div>
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800 mr-2">
              <span className="w-2 h-2 bg-green-600 rounded-full mr-1"></span>
              ≤2h: High confidence
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800 mr-2">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mr-1"></span>
              ≤6h: Medium confidence
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-800">
              <span className="w-2 h-2 bg-red-600 rounded-full mr-1"></span>
              {'>'}6h: Low confidence
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionTable;
